import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardSidebar from '@/components/feature/DashboardSidebar';
import DashboardTopBar from '@/components/feature/DashboardTopBar';
import AdminStatsOverview from './components/AdminStatsOverview';
import PendingProducts from './components/PendingProducts';
import PendingEvents from './components/PendingEvents';
import UsersManagement from './components/UsersManagement';
import EglisesManagement from './components/EglisesManagement';
import LieuxManagement from './components/LieuxManagement';
import AdminCharts from './components/AdminCharts';
import AdminProductFinance from './components/AdminProductFinance';
import AdminEventFinance from './components/AdminEventFinance';
import AdminLieuFinance from './components/AdminLieuFinance';
import AdminMessePayments, { type MessePayment } from './components/AdminMessePayments';
import AdminProfile from './components/AdminProfile';
import AdminAppContent from './components/AdminAppContent';
import type { AdminOverviewCardKey } from './components/AdminStatsOverview';
import type { AdminLog } from '@/mocks/admin';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/lib/api';

type Tab = 'overview' | 'products' | 'events' | 'lieux' | 'eglises' | 'finance-products' | 'finance-events' | 'finance-lieux' | 'messe-payments' | 'users' | 'logs' | 'app-content' | 'profile';
type AdminOrderStatus = 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';

type AdminDashboardData = {
  stats: {
    totalRevenue: number;
    monthlyRevenue: number;
    totalOrders: number;
    pendingOrders: number;
    approvedProducts: number;
    pendingProducts: number;
    rejectedProducts?: number;
    approvedEvents: number;
    pendingEvents: number;
    rejectedEvents?: number;
    totalVendors: number;
    totalOrganizers: number;
    totalUsers: number;
  };
  details: {
    totalRevenue: Array<{ id: string; source: string; title: string; customer: string; email: string; amount: number; status: string; paymentMethod: string; commission?: number; date: string }>;
    totalOrders: Array<{ id: string; commande_id: number; item_index: number; order_number: string; product_name: string; product_image?: string | null; buyer: string; buyer_phone?: string | null; buyer_address?: string | null; quantity: number; amount: number; status: AdminOrderStatus; date: string }>;
    approvedProducts: Array<{ id: number; name: string; owner: string; category: string; price: number; status: string; date: string; image?: string | null; stock?: number }>;
    approvedEvents: Array<{ id: number; user_id?: number; name: string; owner: string; category: string; price: number; status: string; date: string; image?: string | null; totalSeats?: number; ticketsSold?: number }>;
    totalVendors: Array<{ id: number; name: string; email: string; avatar?: string | null; role: string; status: string; phone?: string | null; date: string; location?: string; total_products?: number; total_events?: number; total_sales?: number; total_tickets_sold?: number }>;
    totalOrganizers: Array<{ id: number; name: string; email: string; avatar?: string | null; role: string; status: string; phone?: string | null; date: string; location?: string; total_products?: number; total_events?: number; total_sales?: number; total_tickets_sold?: number }>;
    totalUsers: Array<{ id: number; name: string; email: string; avatar?: string | null; role: string; status: string; phone?: string | null; date: string; location?: string; total_products?: number; total_events?: number; total_sales?: number; total_tickets_sold?: number }>;
  };
  charts: {
    productsStatus: Array<{ name: string; value: number; fill: string }>;
    eventsStatus: Array<{ name: string; value: number; fill: string }>;
    usersRepartition: Array<{ name: string; value: number; fill: string }>;
    revenueMonthly: Array<{ month: string; revenus: number; commandes: number }>;
    weeklyActivity: Array<{ day: string; produits: number; evenements: number; utilisateurs: number }>;
  };
  recentProducts: Array<{ id: number; name: string; vendor: string; price: number; status: string; image?: string | null }>;
  recentEvents: Array<{ id: number; name: string; organizer: string; price: number; status: string; image?: string | null }>;
};

type AdminProductFinanceData = {
  stats: { revenue: number; commission: number; pending: number; transactions: number };
  transactions: Array<{
    id: string;
    reference: string;
    product: { id: number; name: string; category: string; image?: string | null };
    vendor: string;
    buyer: string;
    buyer_email?: string | null;
    buyer_phone?: string | null;
    buyer_address?: string | null;
    quantity: number;
    total: number;
    commission: number;
    payment_method: string;
    status: 'completed' | 'pending' | 'cancelled';
    payment_status: string;
    date: string;
  }>;
};

type AdminEventFinanceData = {
  stats: { revenue: number; commission: number; pending: number; tickets: number };
  transactions: Array<{
    id: string;
    reference: string;
    event: { id: number; name: string; category: string; image?: string | null };
    organizer: string;
    buyer: string;
    buyer_email?: string | null;
    buyer_phone?: string | null;
    buyer_address?: string | null;
    tickets: number;
    total: number;
    commission: number;
    payment_method: string;
    status: 'completed' | 'pending' | 'cancelled';
    payment_status: string;
    date: string;
  }>;
};

type AdminMessePaymentsData = {
  stats: {
    revenue: number;
    pending: number;
    failed: number;
    payments: number;
    completed: number;
    pending_count: number;
    failed_count: number;
  };
  payments: MessePayment[];
};

const ADMIN_COMMISSION_FEE = 500;
type FinanceStatus = AdminProductFinanceData['transactions'][number]['status'];
type EventFinanceStatus = AdminEventFinanceData['transactions'][number]['status'];

const isMissingRouteError = (message: string) => {
  const normalized = message.toLowerCase();
  return normalized.includes('route api/admin/finance-products could not be found')
    || normalized.includes('route [api/admin/finance-products] not defined')
    || normalized.includes('route [admin/finance-products] not defined')
    || normalized.includes('route api/admin/finance-events could not be found')
    || normalized.includes('route [api/admin/finance-events] not defined')
    || normalized.includes('route [admin/finance-events] not defined')
    || normalized.includes('route api/admin/messe-payments could not be found')
    || normalized.includes('route [api/admin/messe-payments] not defined')
    || normalized.includes('route [admin/messe-payments] not defined');
};

const buildFinanceFallbackFromDashboard = (dashboard: AdminDashboardData): AdminProductFinanceData => {
  const transactions = dashboard.details.totalRevenue
    .filter((item) => item.source === 'commande' && (item.status === 'completed' || item.status === 'paid'))
    .map((item, index) => {
      const paymentStatus = 'completed';
      const status: FinanceStatus = 'completed';

      return {
        id: item.id,
        reference: item.id || `CMD-${index + 1}`,
        product: {
          id: index + 1,
          name: item.title,
          category: 'Commande produit',
          image: null,
        },
        vendor: 'Vendeur non disponible',
        buyer: item.customer,
        buyer_email: item.email,
        quantity: 1,
        total: item.amount,
        commission: ADMIN_COMMISSION_FEE,
        payment_method: item.paymentMethod || 'mobile_money',
        status,
        payment_status: paymentStatus,
        date: item.date,
      };
    });

  const completedTransactions = transactions.filter((transaction) => transaction.status === 'completed');

  return {
    stats: {
      revenue: completedTransactions.reduce((sum, transaction) => sum + transaction.total, 0),
      commission: completedTransactions.reduce((sum, transaction) => sum + transaction.commission, 0),
      pending: 0,
      transactions: transactions.length,
    },
    transactions,
  };
};

const buildEventFinanceFallbackFromDashboard = (dashboard: AdminDashboardData): AdminEventFinanceData => {
  const transactions = dashboard.details.totalRevenue
    .filter((item) => item.source === 'ticket' && (item.status === 'completed' || item.status === 'paid'))
    .map((item, index) => {
      const paymentStatus = 'completed';
      const status: EventFinanceStatus = 'completed';

      return {
        id: item.id,
        reference: item.id || `TCK-${index + 1}`,
        event: {
          id: index + 1,
          name: item.title,
          category: 'Billet événement',
          image: null,
        },
        organizer: 'Organisateur non disponible',
        buyer: item.customer,
        buyer_email: item.email,
        tickets: 1,
        total: item.amount,
        commission: ADMIN_COMMISSION_FEE,
        payment_method: item.paymentMethod || 'mobile_money',
        status,
        payment_status: paymentStatus,
        date: item.date,
      };
    });

  const completedTransactions = transactions.filter((transaction) => transaction.status === 'completed');

  return {
    stats: {
      revenue: completedTransactions.reduce((sum, transaction) => sum + transaction.total, 0),
      commission: completedTransactions.reduce((sum, transaction) => sum + transaction.commission, 0),
      pending: 0,
      tickets: completedTransactions.reduce((sum, transaction) => sum + transaction.tickets, 0),
    },
    transactions,
  };
};

const TAB_PATH_MAP: Record<Tab, string> = {
  overview: '/admin',
  products: '/admin/products',
  events: '/admin/events',
  lieux: '/admin/lieux',
  eglises: '/admin/eglises',
  'finance-products': '/admin/finance-products',
  'finance-events': '/admin/finance-events',
  'finance-lieux': '/admin/finance-lieux',
  'messe-payments': '/admin/messe-payments',
  users: '/admin/users',
  logs: '/admin/logs',
  'app-content': '/admin/app-content',
  profile: '/admin/profile',
};

const getTabFromPath = (pathname: string): Tab => {
  if (pathname.includes('/profile')) return 'profile';
  if (pathname.includes('/lieux')) return 'lieux';
  if (pathname.includes('/eglises')) return 'eglises';
  if (pathname.includes('/messe-payments')) return 'messe-payments';
  if (pathname.includes('/finance-lieux')) return 'finance-lieux';
  if (pathname.includes('/finance-events')) return 'finance-events';
  if (pathname.includes('/finance-products')) return 'finance-products';
  if (pathname.includes('/products')) return 'products';
  if (pathname.includes('/events')) return 'events';
  if (pathname.includes('/users')) return 'users';
  if (pathname.includes('/logs')) return 'logs';
  if (pathname.includes('/app-content')) return 'app-content';
  return 'overview';
};

const PRODUCT_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=40&height=40&seq=admin-product-dashboard-fallback&orientation=squarish';
const EVENT_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=event%20stage%20crowd%20conference%20clean&width=40&height=40&seq=admin-event-dashboard-fallback&orientation=squarish';

const handleCardImageError = (event: React.SyntheticEvent<HTMLImageElement>, fallback: string) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = fallback;
};

const formatMoney = (value: number) => value.toLocaleString('fr-FR') + ' FCFA';
const formatDate = (value: string) => new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const getOverviewModalMeta = (key: AdminOverviewCardKey) => {
  switch (key) {
    case 'totalRevenue':
      return { title: 'Toutes les transactions — Revenus totaux', subtitle: 'Vue détaillée complète', icon: 'ri-money-dollar-circle-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-700' };
    case 'totalOrders':
      return { title: 'Toutes les commandes', subtitle: 'Vue détaillée complète', icon: 'ri-shopping-bag-line', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' };
    case 'approvedProducts':
      return { title: 'Produits actifs', subtitle: 'Vue détaillée complète', icon: 'ri-store-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-700' };
    case 'approvedEvents':
      return { title: 'Événements actifs', subtitle: 'Vue détaillée complète', icon: 'ri-calendar-event-line', iconBg: 'bg-violet-100', iconColor: 'text-violet-700' };
    case 'totalVendors':
      return { title: 'Vendeurs', subtitle: 'Vue détaillée complète', icon: 'ri-user-star-line', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' };
    case 'totalOrganizers':
      return { title: 'Organisateurs', subtitle: 'Vue détaillée complète', icon: 'ri-group-line', iconBg: 'bg-slate-100', iconColor: 'text-slate-700' };
  }
};

const getStatusPill = (status: string) => {
  switch (status) {
    case 'approved':
    case 'active':
    case 'completed':
    case 'confirmed':
    case 'delivered':
      return 'bg-emerald-100 text-emerald-700';
    case 'pending':
    case 'shipped':
      return 'bg-amber-100 text-amber-700';
    case 'rejected':
    case 'suspended':
    case 'cancelled':
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'approved':
      return 'Approuvé';
    case 'pending':
      return 'En attente';
    case 'rejected':
      return 'Rejeté';
    case 'active':
      return 'Actif';
    case 'suspended':
      return 'Suspendu';
    case 'completed':
    case 'confirmed':
      return 'Confirmé';
    case 'delivered':
      return 'Livré';
    case 'shipped':
      return 'Expédié';
    case 'cancelled':
      return 'Annulé';
    default:
      return status;
  }
};

const getAdminOrderActions = (status: AdminOrderStatus): Array<{ key: AdminOrderStatus; label: string; icon: string; className: string }> => {
  if (status === 'pending') {
    return [
      { key: 'confirmed', label: 'Confirmer', icon: 'ri-checkbox-circle-line', className: 'bg-teal-600 text-white hover:bg-teal-700' },
      { key: 'cancelled', label: 'Annuler', icon: 'ri-close-circle-line', className: 'bg-red-100 text-red-700 hover:bg-red-200' },
    ];
  }

  if (status === 'confirmed') {
    return [
      { key: 'shipped', label: 'Expédier', icon: 'ri-truck-line', className: 'bg-navy text-white hover:bg-navy-lighter' },
      { key: 'cancelled', label: 'Annuler', icon: 'ri-close-circle-line', className: 'bg-red-100 text-red-700 hover:bg-red-200' },
    ];
  }

  if (status === 'shipped') {
    return [
      { key: 'delivered', label: 'Livré', icon: 'ri-map-pin-2-line', className: 'bg-emerald-600 text-white hover:bg-emerald-700' },
    ];
  }

  return [];
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tab = getTabFromPath(location.pathname);
  const setTab = (newTab: Tab) => navigate(TAB_PATH_MAP[newTab]);
  const displayName = user?.name ?? 'Administrateur';
  const [overviewDetailKey, setOverviewDetailKey] = useState<AdminOverviewCardKey | null>(null);
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(null);
  const [dashboardError, setDashboardError] = useState('');
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [productFinance, setProductFinance] = useState<AdminProductFinanceData | null>(null);
  const [productFinanceLoading, setProductFinanceLoading] = useState(false);
  const [productFinanceError, setProductFinanceError] = useState('');
  const [eventFinance, setEventFinance] = useState<AdminEventFinanceData | null>(null);
  const [eventFinanceLoading, setEventFinanceLoading] = useState(false);
  const [eventFinanceError, setEventFinanceError] = useState('');
  const [messePayments, setMessePayments] = useState<AdminMessePaymentsData | null>(null);
  const [messePaymentsLoading, setMessePaymentsLoading] = useState(false);
  const [messePaymentsError, setMessePaymentsError] = useState('');
  const [orderStatusLoading, setOrderStatusLoading] = useState<string | null>(null);
  const [orderStatusMessage, setOrderStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const updateAdminOrderStatus = async (
    item: AdminDashboardData['details']['totalOrders'][number],
    status: AdminOrderStatus
  ) => {
    setOrderStatusLoading(`${item.id}-${status}`);
    setOrderStatusMessage(null);
    try {
      await apiRequest(`/vendor/orders/${item.commande_id}/${item.item_index}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });

      setDashboard((current) => {
        if (!current) return current;

        return {
          ...current,
          details: {
            ...current.details,
            totalOrders: current.details.totalOrders.map((order) =>
              order.id === item.id ? { ...order, status } : order
            ),
          },
        };
      });
      setOrderStatusMessage({ type: 'success', text: 'Statut de commande mis à jour.' });
    } catch (error) {
      setOrderStatusMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Impossible de mettre à jour le statut.',
      });
    } finally {
      setOrderStatusLoading(null);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await apiRequest<AdminDashboardData>('/admin/dashboard');
        setDashboard(response);
        setDashboardError('');
      } catch (error) {
        console.error('Admin dashboard error:', error);
        setDashboardError(error instanceof Error ? error.message : 'Impossible de charger le dashboard.');
      }
    };

    void loadDashboard();
  }, []);

  useEffect(() => {
    if (tab !== 'logs') {
      return;
    }

    const loadLogs = async () => {
      setLogsLoading(true);
      try {
        const response = await apiRequest<AdminLog[]>('/admin/logs');
        setLogs(response);
        setLogsError('');
      } catch (error) {
        console.error('Admin logs error:', error);
        setLogsError(error instanceof Error ? error.message : 'Impossible de charger les journaux.');
      } finally {
        setLogsLoading(false);
      }
    };

    void loadLogs();
  }, [tab]);

  useEffect(() => {
    if (tab !== 'finance-products') {
      return;
    }

    const loadProductFinance = async () => {
      setProductFinanceLoading(true);
      try {
        const response = await apiRequest<AdminProductFinanceData>('/admin/finance-products');
        setProductFinance(response);
        setProductFinanceError('');
      } catch (error) {
        console.error('Admin product finance error:', error);
        const message = error instanceof Error ? error.message : 'Impossible de charger la finance produits.';

        if (isMissingRouteError(message) && dashboard) {
          setProductFinance(buildFinanceFallbackFromDashboard(dashboard));
          setProductFinanceError('');
        } else {
          setProductFinanceError(message);
        }
      } finally {
        setProductFinanceLoading(false);
      }
    };

    void loadProductFinance();
  }, [dashboard, tab]);

  useEffect(() => {
    if (tab !== 'finance-events') {
      return;
    }

    const loadEventFinance = async () => {
      setEventFinanceLoading(true);
      try {
        const response = await apiRequest<AdminEventFinanceData>('/admin/finance-events');
        setEventFinance(response);
        setEventFinanceError('');
      } catch (error) {
        console.error('Admin event finance error:', error);
        const message = error instanceof Error ? error.message : 'Impossible de charger la finance événements.';

        if (isMissingRouteError(message) && dashboard) {
          setEventFinance(buildEventFinanceFallbackFromDashboard(dashboard));
          setEventFinanceError('');
        } else {
          setEventFinanceError(message);
        }
      } finally {
        setEventFinanceLoading(false);
      }
    };

    void loadEventFinance();
  }, [dashboard, tab]);

  useEffect(() => {
    if (tab !== 'messe-payments') {
      return;
    }

    const loadMessePayments = async () => {
      setMessePaymentsLoading(true);
      try {
        const response = await apiRequest<AdminMessePaymentsData>('/admin/messe-payments');
        setMessePayments(response);
        setMessePaymentsError('');
      } catch (error) {
        console.error('Admin messe payments error:', error);
        setMessePaymentsError(error instanceof Error ? error.message : 'Impossible de charger les paiements de messe.');
      } finally {
        setMessePaymentsLoading(false);
      }
    };

    void loadMessePayments();
  }, [tab]);

  const handleDeleteProductFinanceTransaction = async (transaction: AdminProductFinanceData['transactions'][number]) => {
    if (!window.confirm(`Supprimer définitivement la transaction ${transaction.reference} ?`)) return;

    const [commandeId, itemIndex] = transaction.id.split('-');
    if (!commandeId || itemIndex === undefined) {
      window.alert('Cette transaction ne peut pas être supprimée.');
      return;
    }

    try {
      await apiRequest(`/admin/finance-products/${commandeId}/${itemIndex}`, { method: 'DELETE' });
      setProductFinance(await apiRequest<AdminProductFinanceData>('/admin/finance-products'));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Impossible de supprimer cette transaction.');
    }
  };

  const handleDeleteEventFinanceTransaction = async (transaction: AdminEventFinanceData['transactions'][number]) => {
    if (!window.confirm(`Supprimer définitivement la transaction ${transaction.reference} ?`)) return;

    try {
      await apiRequest(`/admin/finance-events/${transaction.id}`, { method: 'DELETE' });
      setEventFinance(await apiRequest<AdminEventFinanceData>('/admin/finance-events'));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Impossible de supprimer cette transaction.');
    }
  };

  const revenueItems = dashboard?.details?.totalRevenue ?? [];
  const orderItems = dashboard?.details?.totalOrders ?? [];
  const productItems = dashboard?.details?.approvedProducts ?? [];
  const eventItems = dashboard?.details?.approvedEvents ?? [];
  const vendorItems = dashboard?.details?.totalVendors ?? [];
  const organizerItems = dashboard?.details?.totalOrganizers ?? [];
  const overviewModalMeta = overviewDetailKey ? getOverviewModalMeta(overviewDetailKey) : null;

  return (
    <div className="flex h-screen bg-cream overflow-hidden">
      <DashboardSidebar role="admin" userName={displayName} userAvatar={user?.avatar ?? ''} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardTopBar title="Admin Dashboard" subtitle={`Bienvenue, ${displayName}`} userName={displayName} userAvatar={user?.avatar ?? ''} notificationRole="admin" />
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {tab === 'overview' && (
              <>
                {dashboard ? (
                  <>
                    <AdminStatsOverview stats={dashboard.stats} onCardClick={setOverviewDetailKey} />
                    <AdminCharts
                      revenueMonthly={dashboard.charts.revenueMonthly}
                      productsStatus={dashboard.charts.productsStatus}
                      eventsStatus={dashboard.charts.eventsStatus}
                      usersRepartition={dashboard.charts.usersRepartition}
                      weeklyActivity={dashboard.charts.weeklyActivity}
                    />
                  </>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-400">
                    {dashboardError || 'Chargement du dashboard...'}
                  </div>
                )}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {/* Quick pending products */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-gold-100 rounded-lg"><i className="ri-store-line text-gold-dark text-sm"></i></div>
                        <h3 className="text-sm font-bold text-gray-900">Produits récents</h3>
                      </div>
                      <button onClick={() => setTab('products')} className="text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">Voir tout →</button>
                    </div>
                    <div className="space-y-3">
                      {(dashboard?.recentProducts ?? []).map((p) => (
                        <div key={p.id} className="flex items-center gap-3">
                          <img src={p.image || PRODUCT_FALLBACK_IMAGE} alt={p.name} onError={(event) => handleCardImageError(event, PRODUCT_FALLBACK_IMAGE)} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                            <p className="text-xs text-gray-400">{p.vendor} · {p.price.toLocaleString('fr-FR')} FCFA</p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.status === 'pending' ? 'bg-gold-100 text-gold-dark' : 'bg-emerald-100 text-emerald-700'}`}>
                            {p.status === 'pending' ? '🟡 En attente' : '🟢 Approuvé'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Quick pending events */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-violet-100 rounded-lg"><i className="ri-calendar-event-line text-violet-600 text-sm"></i></div>
                        <h3 className="text-sm font-bold text-gray-900">Événements récents</h3>
                      </div>
                      <button onClick={() => setTab('events')} className="text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">Voir tout →</button>
                    </div>
                    <div className="space-y-3">
                      {(dashboard?.recentEvents ?? []).map((e) => (
                        <div key={e.id} className="flex items-center gap-3">
                          <img src={e.image || EVENT_FALLBACK_IMAGE} alt={e.name} onError={(event) => handleCardImageError(event, EVENT_FALLBACK_IMAGE)} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{e.name}</p>
                            <p className="text-xs text-gray-400">{e.organizer} · {e.price.toLocaleString('fr-FR')} FCFA</p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${e.status === 'pending' ? 'bg-gold-100 text-gold-dark' : 'bg-emerald-100 text-emerald-700'}`}>
                            {e.status === 'pending' ? '🟡 En attente' : '🟢 Approuvé'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
            {tab === 'products' && <PendingProducts />}
            {tab === 'events' && <PendingEvents />}
            {tab === 'lieux' && <LieuxManagement />}
            {tab === 'eglises' && <EglisesManagement />}
            {tab === 'finance-products' && (
              productFinance ? (
                <AdminProductFinance stats={productFinance.stats} transactions={productFinance.transactions} onDelete={(transaction) => void handleDeleteProductFinanceTransaction(transaction)} />
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-400">
                  {productFinanceLoading ? 'Chargement de la finance produits...' : productFinanceError || 'Aucune donnée disponible.'}
                </div>
              )
            )}
            {tab === 'finance-events' && (
              eventFinance ? (
                <AdminEventFinance stats={eventFinance.stats} transactions={eventFinance.transactions} onDelete={(transaction) => void handleDeleteEventFinanceTransaction(transaction)} />
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-400">
                  {eventFinanceLoading ? 'Chargement de la finance événements...' : eventFinanceError || 'Aucune donnée disponible.'}
                </div>
              )
            )}
            {tab === 'finance-lieux' && <AdminLieuFinance />}
            {tab === 'messe-payments' && (
              messePayments ? (
                <AdminMessePayments stats={messePayments.stats} payments={messePayments.payments} />
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-400">
                  {messePaymentsLoading ? 'Chargement des paiements de messes...' : messePaymentsError || 'Aucune donnée disponible.'}
                </div>
              )
            )}
            
            {tab === 'users' && <UsersManagement />}
            {tab === 'app-content' && <AdminAppContent />}
            {tab === 'logs' && (
              <div className="bg-white rounded-xl border border-gray-100">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg"><i className="ri-file-list-3-line text-slate-600 text-sm"></i></div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Journal des actions</h3>
                    <p className="text-xs text-gray-400">Historique complet des modérations</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {logsLoading ? (
                    <div className="px-5 py-8 text-sm text-gray-400">Chargement des journaux...</div>
                  ) : null}
                  {!logsLoading && logsError ? (
                    <div className="px-5 py-8 text-sm text-red-500">{logsError}</div>
                  ) : null}
                  {!logsLoading && !logsError && logs.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-gray-400">Aucune action de modération enregistrée pour le moment.</div>
                  ) : null}
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50/50 transition-colors">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${log.result === 'approved' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        <i className={`${log.result === 'approved' ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-close-circle-line text-red-600'} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{log.action}</p>
                        <p className="text-xs text-gray-400 truncate">{log.target} — par {log.admin}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${log.result === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {log.result === 'approved' ? '🟢 Approuvé' : '🔴 Rejeté'}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{new Date(log.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === 'profile' && <AdminProfile />}
          </div>
        </div>
      </div>

      {overviewDetailKey && dashboard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-5xl rounded-[28px] bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${overviewModalMeta?.iconBg} ${overviewModalMeta?.iconColor}`}>
                  <i className={`${overviewModalMeta?.icon} text-base`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-navy">{overviewModalMeta?.title}</h3>
                  <p className="text-sm text-gray-400">{overviewModalMeta?.subtitle}</p>
                </div>
              </div>
              <button onClick={() => setOverviewDetailKey(null)} className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              {overviewDetailKey === 'totalRevenue' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-amber-700">{formatMoney(revenueItems.filter((item) => item.status === 'completed').reduce((sum, item) => sum + item.amount, 0))}</p>
                      <p className="text-sm text-amber-600">Total confirmé</p>
                    </div>
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-emerald-700">{formatMoney(revenueItems.filter((item) => item.status === 'completed').reduce((sum, item) => sum + (item.commission ?? 0), 0))}</p>
                      <p className="text-sm text-emerald-600">Commissions FSN</p>
                    </div>
                    <div className="rounded-2xl bg-slate-100 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-slate-700">{revenueItems.length}</p>
                      <p className="text-sm text-slate-500">Transactions</p>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-gray-100">
                    <div className="max-h-[48vh] overflow-auto">
                      <table className="w-full min-w-[860px]">
                        <thead className="sticky top-0 bg-[#fcfcfd]">
                          <tr className="border-b border-gray-100">
                            {['Type', 'Référence', 'Description', 'Total', 'Commission', 'Date'].map((label) => (
                              <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {revenueItems.map((item) => (
                            <tr key={item.id} className="border-b border-gray-50">
                              <td className="px-4 py-3">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.source === 'commande' ? 'bg-amber-50 text-amber-700' : 'bg-violet-50 text-violet-700'}`}>
                                  {item.source === 'commande' ? 'Produit' : 'Ticket'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs font-semibold text-gold-dark">{item.id}</td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                                <p className="text-xs text-gray-400">{item.customer} · {item.email}</p>
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(item.amount)}</td>
                              <td className="px-4 py-3 text-sm font-bold text-emerald-600">{formatMoney(item.commission ?? 0)}</td>
                              <td className="px-4 py-3 text-xs text-gray-400">{formatDate(item.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {overviewDetailKey === 'totalOrders' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-emerald-700">{orderItems.length}</p>
                      <p className="text-sm text-emerald-600">Total commandes</p>
                    </div>
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-amber-700">{orderItems.filter((item) => item.status === 'pending').length}</p>
                      <p className="text-sm text-amber-600">En attente</p>
                    </div>
                    <div className="rounded-2xl bg-slate-100 px-5 py-4 text-center">
                      <p className="text-3xl font-bold text-slate-700">{orderItems.filter((item) => item.status === 'delivered').length}</p>
                      <p className="text-sm text-slate-500">Livrées</p>
                    </div>
                  </div>

                  {orderStatusMessage ? (
                    <div className={`rounded-2xl px-4 py-3 text-sm font-semibold ${orderStatusMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {orderStatusMessage.text}
                    </div>
                  ) : null}

                  <div className="overflow-hidden rounded-2xl border border-gray-100">
                    <div className="max-h-[48vh] overflow-auto">
                      <table className="w-full min-w-[1080px]">
                        <thead className="sticky top-0 bg-[#fcfcfd]">
                          <tr className="border-b border-gray-100">
                            {['Commande', 'Produit', 'Acheteur', 'Contact', 'Adresse', 'Qté', 'Total', 'Statut', 'Date', 'Actions'].map((label) => (
                              <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {orderItems.map((item) => (
                            <tr key={item.id} className="border-b border-gray-50">
                              <td className="px-4 py-3 text-xs font-semibold text-gold-dark">{item.order_number}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <img src={item.product_image || PRODUCT_FALLBACK_IMAGE} alt={item.product_name} onError={(event) => handleCardImageError(event, PRODUCT_FALLBACK_IMAGE)} className="h-10 w-10 rounded-xl object-cover bg-gray-100" />
                                  <p className="text-sm font-semibold text-gray-900">{item.product_name}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{item.buyer}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{item.buyer_phone || 'Non renseigné'}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{item.buyer_address || 'Non renseignée'}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.quantity}</td>
                              <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(item.amount)}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusPill(item.status)}`}>{getStatusLabel(item.status)}</span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-400">{formatDate(item.date)}</td>
                              <td className="px-4 py-3">
                                {getAdminOrderActions(item.status).length ? (
                                  <div className="flex flex-wrap gap-2">
                                    {getAdminOrderActions(item.status).map((action) => {
                                      const loadingKey = `${item.id}-${action.key}`;

                                      return (
                                        <button
                                          key={action.key}
                                          onClick={() => void updateAdminOrderStatus(item, action.key)}
                                          disabled={orderStatusLoading !== null}
                                          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${action.className}`}
                                        >
                                          {orderStatusLoading === loadingKey ? (
                                            <i className="ri-loader-4-line animate-spin"></i>
                                          ) : (
                                            <i className={action.icon}></i>
                                          )}
                                          {action.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-xs font-semibold text-gray-400">Aucune action</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {overviewDetailKey === 'approvedProducts' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-emerald-700">{productItems.filter((item) => item.status === 'approved').length}</p><p className="text-sm text-emerald-600">Approuvés</p></div>
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-amber-700">{productItems.filter((item) => item.status === 'pending').length}</p><p className="text-sm text-amber-600">En attente</p></div>
                    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-red-700">{productItems.filter((item) => item.status === 'rejected').length}</p><p className="text-sm text-red-600">Rejetés</p></div>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-gray-100">
                    <div className="max-h-[48vh] overflow-auto">
                      <table className="w-full min-w-[900px]">
                        <thead className="sticky top-0 bg-[#fcfcfd]">
                          <tr className="border-b border-gray-100">
                            {['Produit', 'Vendeur', 'Catégorie', 'Prix', 'Stock', 'Statut', 'Date'].map((label) => (
                              <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {productItems.map((item) => (
                            <tr key={item.id} className="border-b border-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <img src={item.image || PRODUCT_FALLBACK_IMAGE} alt={item.name} onError={(event) => handleCardImageError(event, PRODUCT_FALLBACK_IMAGE)} className="h-11 w-11 rounded-xl object-cover bg-gray-100" />
                                  <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{item.owner}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{item.category}</td>
                              <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(item.price)}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.stock ?? 0}</td>
                              <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusPill(item.status)}`}>{getStatusLabel(item.status)}</span></td>
                              <td className="px-4 py-3 text-xs text-gray-400">{formatDate(item.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {overviewDetailKey === 'approvedEvents' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-emerald-700">{eventItems.filter((item) => item.status === 'approved').length}</p><p className="text-sm text-emerald-600">Approuvés</p></div>
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-amber-700">{eventItems.filter((item) => item.status === 'pending').length}</p><p className="text-sm text-amber-600">En attente</p></div>
                    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-red-700">{eventItems.filter((item) => item.status === 'rejected').length}</p><p className="text-sm text-red-600">Rejetés</p></div>
                  </div>

                  <div className="space-y-3">
                    {eventItems.map((item) => {
                      const seatRatio = item.totalSeats && item.totalSeats > 0 ? Math.min(100, Math.round(((item.ticketsSold ?? 0) / item.totalSeats) * 100)) : 0;
                      return (
                        <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                          <img src={item.image || EVENT_FALLBACK_IMAGE} alt={item.name} onError={(event) => handleCardImageError(event, EVENT_FALLBACK_IMAGE)} className="h-16 w-16 rounded-2xl object-cover bg-gray-100" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-navy">{item.name}</p>
                            <p className="text-xs text-gray-500">{item.category} · {item.owner}</p>
                            <div className="mt-2 flex items-center gap-2">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-amber-100">
                                <div className="h-full rounded-full bg-gold" style={{ width: `${seatRatio}%` }} />
                              </div>
                              <span className="text-xs text-gray-400">{item.ticketsSold ?? 0}/{item.totalSeats ?? 0}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-navy">{formatMoney(item.price)}</p>
                            <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusPill(item.status)}`}>{getStatusLabel(item.status)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {overviewDetailKey === 'totalVendors' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-cyan-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-cyan-700">{vendorItems.length}</p><p className="text-sm text-cyan-600">Total vendeurs</p></div>
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-emerald-700">{vendorItems.filter((item) => item.status === 'active').length}</p><p className="text-sm text-emerald-600">Actifs</p></div>
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-amber-700">{vendorItems.filter((item) => item.status === 'pending').length}</p><p className="text-sm text-amber-600">En attente</p></div>
                  </div>

                  <div className="space-y-3">
                    {vendorItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-sm font-bold text-navy">{item.name.slice(0, 2).toUpperCase()}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-navy">{item.name}</p>
                          <p className="truncate text-xs text-gray-500">{item.location || 'Localisation non renseignée'}</p>
                          <p className="truncate text-xs text-gray-400">{item.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-navy">{formatMoney(item.total_sales ?? 0)}</p>
                          <p className="text-xs text-gray-400">{item.total_products ?? 0} produits</p>
                          <span className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusPill(item.status)}`}>{getStatusLabel(item.status)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {overviewDetailKey === 'totalOrganizers' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl bg-slate-100 px-5 py-4 text-center"><p className="text-3xl font-bold text-slate-700">{organizerItems.length}</p><p className="text-sm text-slate-500">Total organisateurs</p></div>
                    <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-emerald-700">{organizerItems.filter((item) => item.status === 'active').length}</p><p className="text-sm text-emerald-600">Actifs</p></div>
                    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center"><p className="text-3xl font-bold text-amber-700">{organizerItems.filter((item) => item.status === 'pending').length}</p><p className="text-sm text-amber-600">En attente</p></div>
                  </div>

                  <div className="space-y-3">
                    {organizerItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-sm font-bold text-white">{item.name.slice(0, 2).toUpperCase()}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-navy">{item.name}</p>
                          <p className="truncate text-xs text-gray-500">{item.location || 'Localisation non renseignée'}</p>
                          <p className="truncate text-xs text-gray-400">{item.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-navy">{item.total_events ?? 0} événements</p>
                          <p className="text-xs text-gray-400">{item.total_tickets_sold ?? 0} tickets</p>
                          <span className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusPill(item.status)}`}>{getStatusLabel(item.status)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
